import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid2 from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { UserCard, SectionHeader } from 'molecules';
import { PublicUserInfo } from '@elevator/ui';

/**
 * SearchResultUserList Component
 *
 * Displays a list of search results for users. Shows user cards in a grid layout
 * with optional title header. Handles empty state when no users are found.
 *
 * Features:
 * - User cards grid display
 * - Optional section title via site `SectionHeader` (matches Search Posts heading)
 * - Empty state handling
 * - Responsive layout
 *
 * Used in:
 * - Search results page (users tab)
 *
 * @param props - Component props
 * @param props.users - Array of user information to display
 * @param props.title - Optional title text displayed above the user list
 */
const SearchResultUserList = (props: SearchResultUserListPropTypes) => {
  const { title, users, layout = 'list' } = props;

  if (users.length === 0) {
    return (
      <Box
        data-component="SearchResultUserList"
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="center"
        paddingTop={6}
      >
        <Typography variant={'h6'} color={'textSecondary'}>
          No users found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} data-component="SearchResultUserList">
      {title && (
        <SectionHeader
          title={title}
          titleVariant="h5"
          titleComponent="h2"
          divider={false}
        />
      )}
      {layout === 'grid' ? (
        <Grid2 container spacing={2}>
          {users.map((user) => (
            <Grid2 key={user.preferredUsername} size={{ xs: 12, sm: 6, md: 4 }}>
              <UserCard
                variant="fullWidth"
                user={user}
                socialMedia={user.socialMedia}
                name={user.displayName || user.preferredUsername}
              />
            </Grid2>
          ))}
        </Grid2>
      ) : (
        <Stack spacing={2}>
          {users.map((user) => (
            <UserCard
              key={user.preferredUsername}
              variant="fullWidth"
              user={user}
              socialMedia={user.socialMedia}
              name={user.displayName || user.preferredUsername}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export type SearchResultUserListPropTypes = {
  title?: string;
  users: PublicUserInfo[];
  /** `grid` uses a responsive multi-column layout from `sm` up; `list` stacks cards (default). */
  layout?: 'list' | 'grid';
};

export default SearchResultUserList;
